import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';

import OrderIcon from '@/components/OrderIcon/OrderIcon';
import OrderOptionsList from '@/components/OrderOptionList/OrderOptionsList';
import InspectionAttach from '@/inspectionAttach/InspectionAttach';
import PropertyAttach from '@/inspectionAttach/PropertyAttach';
import ClientOrderFormPage from '@/orderForm/ClientOrderForm/ClientOrderFormPage/ClientOrderFormPage';
import { RV_GROUP_CLIENT_PANEL_BASE_URL } from '@/config';

import { useUser } from '@/contexts/UserContext';
import ModalComponent from '@/components/common/Modal/ModalComponent';

const FormRoute = () => {
  const { orderType, previousId, propertyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <ClientOrderFormPage
      newOrderType={orderType}
      inspectionIcon={<OrderIcon orderType={orderType} fontSize="calc(16px + 0.8vw)" />}
      cancelNewOrder={() => navigate('/orders/new')}
      previousOrderId={previousId || null}
      previousOrderFile={location.state?.previousOrderFile || null}
      previousPropertyId={propertyId || null}
    />
  );
};

const AttachRoute = ({ attachType, intl }) => {
  const { orderType } = useParams();
  const navigate = useNavigate();

  const continueWithoutAttachment = () => navigate(`/orders/new/${orderType}/form`);

  if (attachType === 'property') {
    const orderSelectProperty = (option) => {
      navigate(`/orders/new/${orderType}/form/property/${option?.public_id}`);
    };

    return (
      <PropertyAttach
        header={<FormattedMessage id="selectPropertyHeader" />}
        subHeader={<FormattedMessage id="selectPropertySubHeader" />}
        searchPlaceholder={intl.formatMessage({ id: 'selectProperty' })}
        apiUrl="client-properties"
        clientPanel
        handleSearchConfirm={orderSelectProperty}
        handleContinueWithouAttachment={continueWithoutAttachment}
      />
    );
  }

  // attachType === 'previous'
  let previousOrderType = null;
  let header = null;
  let previousType = null;

  if (orderType === 'saida') {
    previousOrderType = "['entrada','aditivo']";
    header = 'inspection.saida';
    previousType = intl.formatMessage({ id: 'entradaOrAditivo' });
  } else if (orderType === 'saidaCurtaTemporada') {
    previousOrderType = "['entradaCurtaTemporada']";
    header = 'inspection.saidaCurtaTemporada';
    previousType = intl.formatMessage({ id: 'entradaCurtaTemporada' });
  } else if (orderType === 'conferencia') {
    previousOrderType = "['saida','conferencia']";
    header = 'inspection.conferencia';
    previousType = intl.formatMessage({ id: 'saidaOrConferencia' });
  } else if (orderType === 'conferenciaFinalDeObra') {
    previousOrderType = "['finalDeObra','conferenciaFinalDeObra']";
    header = 'inspection.conferenciaFinalDeObra';
    previousType = intl.formatMessage({ id: 'finalDeObraOrConferenciaFinalDeObra' });
  } else if (orderType === 'conferenciaEntrega') {
    previousOrderType = "['entrega','conferenciaEntrega']";
    header = 'inspection.conferenciaEntrega';
    previousType = intl.formatMessage({ id: 'entregaOrConferenciaEntrega' });
  } else if (orderType === 'aditivo') {
    previousOrderType = 'entrada';
    header = 'inspection.aditivo';
    previousType = intl.formatMessage({ id: previousOrderType });
  } else if (orderType === 'revistoriaBrookfield') {
    previousOrderType = "['brookfield', 'revistoriaBrookfield']";
    header = 'inspection.brookfield';
    previousType = intl.formatMessage({ id: 'brookfieldOrRevistoria' });
  } else if (orderType === 'acompanhamentoObraMM') {
    previousOrderType = 'acompanhamentoObraMM';
    header = 'inspection.acompanhamentoObraMM';
    previousType = intl.formatMessage({ id: previousOrderType });
  } else if (orderType === 'acompanhamentoUrba') {
    previousOrderType = 'acompanhamentoUrba';
    header = 'inspection.acompanhamentoUrba';
    previousType = intl.formatMessage({ id: previousOrderType });
  } else if (orderType === 'auditoriaCbre') {
    previousOrderType = 'auditoriaCbre';
    header = 'inspection.auditoriaCbre';
    previousType = intl.formatMessage({ id: previousOrderType });
  } else if (orderType === 'intermediariaBHomy') {
    previousOrderType = "['entradaBHomy','intermediariaBHomy']";
    header = 'inspection.intermediariaBHomy';
    previousType = intl.formatMessage({ id: 'entradaBHomyOrIntermediariaBHomy' });
  }

  const orderAttachPanel = (option) => {
    navigate(`/orders/new/${orderType}/form/${option.id}`);
  };

  const orderAttachFile = (fileExtension, originalName, remoteName) => {
    const previousOrderFile = {
      file: {
        original_name: originalName,
        remote_name: remoteName,
        extension: fileExtension,
      },
    };
    navigate(`/orders/new/${orderType}/form`, { state: { previousOrderFile } });
  };

  return (
    <InspectionAttach
      header={<FormattedMessage id={header} />}
      subHeader={<FormattedMessage id="inspectionAttach.entry" values={{ type: previousType }} />}
      searchPlaceholder={intl.formatMessage(
        { id: 'inspectionAttach.searchEntry' },
        { type: previousType?.toLowerCase() || '' }
      )}
      apiUrl={`/orders?type=${previousOrderType}&status=sent&search=`}
      clientPanel
      handleSearchConfirm={orderAttachPanel}
      handleFileConfirm={orderAttachFile}
      handleContinueWithouAttachment={continueWithoutAttachment}
      previousType={previousType}
      newOrderType={orderType}
    />
  );
};

const ClientNewOrder = ({ features }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [showPermissionModal, setShowPermissionModal] = React.useState(false);
  const { activeIntegrations = [] } = useUser();

  const handleOrderOptionSelect = (option) => {
    if (
      [
        'saida',
        'saidaCurtaTemporada',
        'conferencia',
        'aditivo',
        'acompanhamentoObraMM',
        'acompanhamentoUrba',
        'conferenciaFinalDeObra',
        'conferenciaEntrega',
        'intermediariaBHomy',
        'revistoriaBrookfield',
        'auditoriaCbre'
      ].includes(option)
    ) {
      navigate(`${option}/previous`);
    } else if (option === 'reparoFacil' || option === 'marketplace_reparofacil') {
      const hasReparoFacilPermission = features.includes('VISTORIA_REPARO_FACIL');

      if (!hasReparoFacilPermission) {
        setShowPermissionModal(true);
        return;
      }

      const isIntegrationActive = activeIntegrations.includes('reparofacil');

      if (!isIntegrationActive) {
        navigate('/marketplace/reparofacil');
        return;
      }

      const accessToken = window.sessionStorage.getItem('accessToken');
      const tokenType = window.sessionStorage.getItem('tokenType');
      const path = 'inspections/orders/new/marketplace_reparofacil/form';
      window.location.href = `${RV_GROUP_CLIENT_PANEL_BASE_URL}/${path}/#access_token=${accessToken}&token_type=${tokenType}&expires_in=&path=${path}`;
    } else if (option.startsWith('marketplace_') && option !== 'marketplace_captura360') {
      navigate(`${option}/form`);
    } else {
      navigate(`${option}/property`);
    }
  };

  return (
    <>
      <Routes>
        <Route path="/" element={<OrderOptionsList features={features} handleOrderOptionSelect={handleOrderOptionSelect} withDescription />} />
        <Route path=":orderType/previous" element={<AttachRoute attachType="previous" intl={intl} />} />
        <Route path=":orderType/property" element={<AttachRoute attachType="property" intl={intl} />} />
        <Route path=":orderType/form" element={<FormRoute />} />
        <Route path=":orderType/form/:previousId" element={<FormRoute />} />
        <Route path=":orderType/form/property/:propertyId" element={<FormRoute />} />
      </Routes>

      {showPermissionModal && (
        <ModalComponent handleClick={() => setShowPermissionModal(false)} className="w-[90%] max-w-[500px]">
          <div className="flex flex-col gap-4 text-center">
            <h3 className="text-lg font-bold text-gray-900">
              <FormattedMessage id="reparofacil.permissionModal.title" defaultMessage="Funcionalidade Não Habilitada" />
            </h3>
            <p className="text-sm text-gray-600">
              <FormattedMessage
                id="reparofacil.permissionModal.message"
                defaultMessage="Entre em contato com sua unidade Rede Vistorias para habilitar a funcionalidade, em seguida, na tela de integrações, informe sua api key."
              />
            </p>
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                className="rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                onClick={() => setShowPermissionModal(false)}
              >
                <FormattedMessage id="close" defaultMessage="Fechar" />
              </button>
            </div>
          </div>
        </ModalComponent>
      )}
    </>
  );
};

ClientNewOrder.propTypes = {
  features: PropTypes.array.isRequired,
};

export default ClientNewOrder;
